import type { Maybe } from '@infinex-private/ts-utils/types';

/**
 * Returns the aria-current value for a given pathname and href.
 * @param pathname - The current pathname.
 * @param href - The item's href.
 */
export function ariaCurrentPage(pathname: string | null, href: string) {
  if (!pathname) return 'false';

  const normalizedPathname = normalizePath(pathname);
  const normalizedHref = normalizePath(href);

  // only exact matches receive "page"
  // https://www.w3.org/TR/wai-aria-1.2/#aria-current
  if (normalizedPathname === normalizedHref) return 'page';

  // pathname is a child of href
  if (href !== '/' && normalizedPathname.startsWith(normalizedHref)) {
    // avoid substring matches
    const nextChar = normalizedPathname.charAt(normalizedHref.length);
    if (nextChar === '/' || nextChar === '') {
      return 'true';
    }
  }

  return 'false';
}

/** Remove trailing slashes, except for root. */
function normalizePath(path: string) {
  return path === '/' ? path : path.replace(/\/$/, '');
}

/**
 * Thin wrapper around `join()` that filters out “falsy” values and returns
 * `undefined` if the result is empty.
 */
export function joinIds(...ids: Maybe<string>[]) {
  // cheap length check before filtering
  if (!ids || ids.length === 0) {
    return undefined;
  }

  const filteredIds = ids.filter(Boolean);
  if (filteredIds.length === 0) {
    return undefined;
  }

  return filteredIds.join(' ');
}
